import {
  Button,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import ProfileDocumentsUploadModal from "./ProfileDocumentUploadModal";
import ProfileDocumentsUpdateModal from "./ProfileDocumentUpdateModal";
import {
  ActionByType,
  ProfileDocument,
  ProfileDocumentOperationType,
  ProfileDocumentTypeLabel,
} from "../../common/ProfileDocument";
import convertDateToString from "../../util/ConvertDateToString";
import { Delete, FileOpen } from "@mui/icons-material";
import {
  useDeleteProfileDocumentMutation,
  useDownloadProfileDocumentMutation,
  useGetProfileDocumentsQuery,
} from "../../redux/api/profileDocument";
import { formatDateString } from "../../common/FormatDateString";

interface ProfileDocumentsProps {
  profileId: number;
  isViewOnly: boolean;
  parentCallback: Function;
  actionByType: ActionByType;
}

const ProfileDocuments = (props: ProfileDocumentsProps) => {
  const [profileDocuments, setProfileDocuments] = useState<ProfileDocument[]>(
    []
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currDocId, setCurrDocId] = useState<number>(); // to keep track of which document the delete confirmation dialog is for

  const {
    data: documentsFromApi,
    isLoading,
    isFetching,
    refetch,
  } = useGetProfileDocumentsQuery(props.profileId);

  const [deleteProfileDocument] = useDeleteProfileDocumentMutation();
  const [downloadProfileDocument] = useDownloadProfileDocumentMutation();

  useEffect(() => {
    if (!isFetching && !isLoading) {
      let res: any = JSON.parse(JSON.stringify(documentsFromApi));
      setProfileDocuments(
        res.map((doc: any) => {
          // convert null values to empty string
          Object.keys(doc).forEach(
            (key) => (doc[key] = doc[key] === null ? "" : doc[key])
          );
          // convert dates to string
          if (doc["issueDate"] !== undefined && doc["issueDate"] !== "") {
            doc["issueDate"] = convertDateToString(new Date(doc["issueDate"]));
          }
          if (
            doc["expirationDate"] !== undefined &&
            doc["expirationDate"] !== ""
          ) {
            doc["expirationDate"] = convertDateToString(
              new Date(doc["expirationDate"])
            );
          }
          return doc as ProfileDocument;
        })
      );
    }
  }, [documentsFromApi, isFetching, isLoading]);

  const handleUpload = () => {
    refetch();
    props.parentCallback(ProfileDocumentOperationType.UPLOAD);
  };

  const deleteDocument = async () => {
    if (currDocId === undefined) {
      return;
    }
    const response = await deleteProfileDocument({
      profileDocumentId: currDocId.toString(),
      actionByType: props.actionByType,
    });
    if ("error" in response) {
      // TODO: error handling/surfacing
    }
    refetch();
    props.parentCallback(ProfileDocumentOperationType.DELETE);
    handleClose();
  };

  // const handleDownload = async (url: string, fileName: string) => {
  //   const response = await downloadProfileDocument({
  //     fileKey: url,
  //   });
  //   if ("error" in response === false) {
  //     // create a temporary URL and link and simulate a click to trigger the browser download behavior
  //     // @ts-ignore - if there is no error object in the response, we know it must have a Blob 'data' attribute
  //     const url = window.URL.createObjectURL(response.data);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.setAttribute("download", fileName);
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);

  //     props.parentCallback(ProfileDocumentOperationType.DOWNLOAD);
  //   }
  // };

  const handleView = async (url: string, contentType: string, fileName: string) => {
    const response = await downloadProfileDocument({
      fileKey: url,
    });
    if ("error" in response === false) {


      // @ts-ignore - if there is no error object in the response, we know it must have a Blob 'data' attribute
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const inlineTypes = [
        'application/pdf',
        'image/png',
        'image/jpeg',
      ];

      if (inlineTypes.includes(contentType)) {
        window.open(url, "_blank");
      } else {
        const link = document.createElement("a");
        
        link.href = url;
        link.target = "_blank"
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const handleOpen = (documentId: number) => {
    setCurrDocId(documentId);
    setConfirmDelete(true);
  };

  const handleClose = () => {
    setConfirmDelete(false);
  };

  const handleUpdate = () => {
    refetch();
    props.parentCallback(ProfileDocumentOperationType.EDIT);
  };

  return (
    <>
      <TableContainer>
        <Table className="documents-table">
          <TableHead>
            <TableRow>
              <TableCell>TYPE</TableCell>
              <TableCell>FILE NAME</TableCell>
              <TableCell>ISSUE DATE</TableCell>
              <TableCell>EXPIRATION DATE</TableCell>
              {!props.isViewOnly && <TableCell />}
              {/* empty cell for icons */}
            </TableRow>
          </TableHead>
          <TableBody>
            {profileDocuments.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell>{ProfileDocumentTypeLabel[doc.type]}</TableCell>
                <TableCell>{doc.displayName}</TableCell>
                <TableCell>{formatDateString(doc.issueDate)}</TableCell>
                <TableCell>{formatDateString(doc.expirationDate)}</TableCell>
                {!props.isViewOnly ? (
                  <TableCell className="document-actions">
                    <Button
                      className="blue-link-button"
                      onClick={() =>
                        handleView(doc.fileUrl, doc.contentType, doc.displayName)
                      }
                    >
                      <FileOpen />
                      View
                    </Button>
                    <ProfileDocumentsUpdateModal
                      document={doc}
                      onUpdate={handleUpdate}
                      actionByType={props.actionByType}
                    />
                    <Button
                      className="blue-link-button"
                      onClick={() => handleOpen(doc.id)}
                    >
                      <Delete />
                      Delete
                    </Button>
                    {/* <Button
                      className="blue-link-button"
                      onClick={() =>
                        handleDownload(doc.fileUrl, doc.displayName)
                      }
                    >
                      <CloudDownload />
                      Download
                    </Button> */}
                  </TableCell>
                ) :
                  <TableCell className="document-actions">
                    <Button
                      className="blue-link-button"
                      onClick={() =>
                        handleView(doc.fileUrl, doc.contentType, doc.displayName)
                      }
                    >
                      <FileOpen />
                      View
                    </Button>
                  </TableCell>
                }
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!props.isViewOnly && (
        <ProfileDocumentsUploadModal
          profileId={props.profileId}
          onUpload={handleUpload}
          actionByType={props.actionByType}
        />
      )}
      <Dialog
        open={confirmDelete}
        onClose={handleClose}
        aria-labelledby="modal-title"
      >
        <Typography variant="h3" id="modal-title">
          Delete Document
        </Typography>
        <p>Are you sure you would like to delete this document?</p>
        <div className="modal-footer">
          <Button className="outline-button-small" onClick={deleteDocument}>
            Confirm
          </Button>
          <Button className="blue-button-small" onClick={handleClose}>
            Cancel
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default ProfileDocuments;
